import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription, UpgradePromptDialog } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Calendar, AlertCircle, Lock, Camera, X, Loader2 } from 'lucide-react';

const HORSE_TABS = [
  { value: 'in_work', label: 'In Work' },
  { value: 'spelling', label: 'Spelling' },
  { value: 'breeding', label: 'Breeding' },
  { value: 'youngstock', label: 'Youngstock' },
  { value: 'former', label: 'Former' }
];

export const HorsesPage = () => {
  const { user, token } = useAuth();
  const { canAddEdit, canAddHorse, requireSubscription, getHorseLimit, isPro } = useSubscription();
  const [searchParams] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Profile photo state for new horse
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const profilePhotoInputRef = useRef(null);

  // Compute default owner name from user
  const defaultOwnerName = user?.use_business_as_owner && user?.business_name 
    ? user.business_name 
    : user?.name || '';

  const [newHorse, setNewHorse] = useState({
    registered_name: '',
    stable_name: '',
    breed: '',
    colour: '',
    sex: '',
    dob: '',
    height: '',
    microchip: '',
    registration_number: '',
    registration_society: '',
    sire: '',
    dam: '',
    breeder: '',
    owner: '',
    horse_tab: 'in_work'
  });

  const fetchHorses = async () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const headers = { 'Content-Type': 'application/json' };
    if (activeAccountId) headers['X-Active-Account'] = activeAccountId;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(`${API}/horses`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setHorses(data);
      }
    } catch (error) {
      console.error('Failed to fetch horses:', error);
      toast.error('Failed to load horses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHorses();
  }, [token]);

  // Set default owner when dialog opens
  useEffect(() => {
    if (addDialogOpen && defaultOwnerName) {
      setNewHorse(prev => ({ ...prev, owner: prev.owner || defaultOwnerName }));
    }
  }, [addDialogOpen, defaultOwnerName]);

  useEffect(() => {
    // Handle pre-selected horse from redirect
    const selectedHorseId = searchParams.get('selected');
    if (selectedHorseId && horses.length > 0) {
      // Scroll to horse card
      const card = document.querySelector(`[data-testid="horse-card-${selectedHorseId}"]`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [searchParams, horses]);

  const handleAddHorse = async (e) => {
    e.preventDefault();
    if (!newHorse.registered_name || !newHorse.stable_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const headers = { 'Content-Type': 'application/json' };
    if (activeAccountId) headers['X-Active-Account'] = activeAccountId;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      // Step 1: Create the horse
      const response = await fetch(`${API}/horses`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newHorse)
      });

      if (response.ok) {
        const horse = await response.json();
        
        // Step 2: Upload profile photo if one was selected
        if (profilePhotoFile) {
          try {
            const formData = new FormData();
            formData.append('file', profilePhotoFile);
            
            const uploadHeaders = {};
            if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
            if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
            
            const uploadResponse = await fetch(`${API}/upload/profile/${horse.horse_id}`, {
              method: 'POST',
              headers: uploadHeaders,
              body: formData
            });
            
            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              horse.profile_photo = uploadData.file_url;
            }
          } catch (uploadError) {
            console.error('Failed to upload profile photo:', uploadError);
            // Don't fail the whole operation, just notify
            toast.info('Horse created, but photo upload failed. You can add a photo from the horse profile.');
          }
        }
        
        setHorses([...horses, horse]);
        setAddDialogOpen(false);
        // Reset form
        setNewHorse({
          registered_name: '', stable_name: '', breed: '', colour: '', sex: '',
          dob: '', height: '', microchip: '', registration_number: '',
          registration_society: '', sire: '', dam: '', breeder: '', owner: defaultOwnerName,
          horse_tab: 'in_work'
        });
        setProfilePhotoFile(null);
        setProfilePhotoPreview(null);
        toast.success('Horse added successfully!');
      }
    } catch (error) {
      console.error('Failed to add horse:', error);
      toast.error('Failed to add horse');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle profile photo selection
  const handleProfilePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      setProfilePhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Clear profile photo
  const clearProfilePhoto = () => {
    setProfilePhotoFile(null);
    setProfilePhotoPreview(null);
    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = '';
    }
  };

  // Group horses by tab - use horse_tab field, default to 'in_work' for legacy horses
  const getHorsesByTab = (tabValue) => {
    return horses.filter(h => {
      const horseTab = h.horse_tab || (h.status === 'former' ? 'former' : 'in_work');
      return horseTab === tabValue;
    });
  };

  // Count horses for each tab
  const tabCounts = HORSE_TABS.reduce((acc, tab) => {
    acc[tab.value] = getHorsesByTab(tab.value).length;
    return acc;
  }, {});

  // Count current (non-former) horses for limit check
  const currentCount = horses.filter(h => {
    const horseTab = h.horse_tab || (h.status === 'former' ? 'former' : 'in_work');
    return horseTab !== 'former';
  }).length;

  // Check if user can add more horses based on their subscription limit
  const canAddMoreHorses = canAddHorse(currentCount);
  const horseLimit = getHorseLimit();

  const handleAddHorseClick = () => {
    if (!canAddEdit()) {
      requireSubscription();
      return;
    }
    if (!canAddMoreHorses && horseLimit !== -1) {
      setShowUpgradeDialog(true);
      return;
    }
    setAddDialogOpen(true);
  };

  const filterHorses = (horseList) => {
    if (!searchTerm) return horseList;
    const term = searchTerm.toLowerCase();
    return horseList.filter(h => 
      h.stable_name?.toLowerCase().includes(term) ||
      h.registered_name?.toLowerCase().includes(term) ||
      h.breed?.toLowerCase().includes(term)
    );
  };

  const HorseCard = ({ horse }) => (
    <Link to={`/horses/${horse.horse_id}`} data-testid={`horse-card-${horse.horse_id}`}>
      <Card className="horse-card card-hover cursor-pointer overflow-hidden border-0 bg-white shadow-card h-full">
        <div className="aspect-square relative bg-muted max-h-24">
          {horse.profile_photo ? (
            <img 
              src={horse.profile_photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${horse.profile_photo}` : horse.profile_photo}
              alt={horse.stable_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-forest/10 to-saddle/10">
              <span className="text-2xl">🐴</span>
            </div>
          )}
          {horse.is_deceased && (
            <div className="absolute top-1 right-1">
              <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Deceased</Badge>
            </div>
          )}
        </div>
        <CardContent className="p-3">
          <h3 className="font-semibold text-sm font-serif truncate">{horse.stable_name}</h3>
          <p className="text-xs text-muted-foreground truncate">{horse.registered_name}</p>
          {(horse.breed || horse.colour) && (
            <p className="text-xs text-muted-foreground truncate mt-1">
              {horse.breed}{horse.breed && horse.colour && ' • '}{horse.colour}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );

  const renderTabContent = (tabValue) => {
    const tabHorses = filterHorses(getHorsesByTab(tabValue));
    const isFormer = tabValue === 'former';
    
    if (tabHorses.length === 0) {
      return (
        <div className="text-center py-16">
          {tabValue === 'in_work' && horses.length === 0 ? (
            <>
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🐴</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">No horses yet</h3>
              <p className="text-muted-foreground mb-4">Add your first horse to get started</p>
              <Button 
                onClick={handleAddHorseClick} 
                className="bg-forest hover:bg-forest-dark gap-2"
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" />
                Add Horse
              </Button>
            </>
          ) : (
            <div className="text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No horses in {HORSE_TABS.find(t => t.value === tabValue)?.label}</p>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {tabHorses.map((horse, i) => (
          <div key={horse.horse_id} className={`animate-slideUp ${isFormer ? 'opacity-80' : ''}`} style={{ animationDelay: `${i * 0.05}s` }}>
            <HorseCard horse={horse} />
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-10 w-32 skeleton rounded-lg" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="horses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">My Horses</h1>
            <p className="text-muted-foreground mt-1">
              {currentCount} current • {tabCounts.former || 0} former
            </p>
          </div>
          
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                data-testid="add-horse-btn"
                onClick={(e) => {
                  if (!canAddEdit() || (!canAddMoreHorses && horseLimit !== -1)) {
                    e.preventDefault();
                    handleAddHorseClick();
                  }
                }}
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" />
                Add Horse
                {horseLimit > 0 && canAddEdit() && (
                  <span className="text-xs opacity-75">({currentCount}/{horseLimit})</span>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Add New Horse</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddHorse} className="space-y-4 mt-4">
                {/* Profile Photo Upload */}
                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="relative w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 hover:border-forest/50 transition-colors cursor-pointer overflow-hidden bg-muted/30 flex items-center justify-center"
                      onClick={() => profilePhotoInputRef.current?.click()}
                    >
                      {profilePhotoPreview ? (
                        <>
                          <img 
                            src={profilePhotoPreview} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-1 right-1 h-6 w-6 bg-black/50 hover:bg-black/70 text-white rounded-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearProfilePhoto();
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <div className="text-center p-2">
                          <Camera className="w-8 h-8 mx-auto text-muted-foreground/50" />
                          <span className="text-xs text-muted-foreground">Add photo</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={profilePhotoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePhotoSelect}
                      className="hidden"
                    />
                    <p className="text-xs text-muted-foreground flex-1">
                      Optional: Add an ID photo for your horse. You can also add this later from the horse's profile.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="registered_name">Registered Name *</Label>
                    <Input 
                      id="registered_name"
                      value={newHorse.registered_name}
                      onChange={(e) => setNewHorse({...newHorse, registered_name: e.target.value})}
                      required
                      data-testid="new-horse-registered-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stable_name">Stable Name *</Label>
                    <Input 
                      id="stable_name"
                      value={newHorse.stable_name}
                      onChange={(e) => setNewHorse({...newHorse, stable_name: e.target.value})}
                      required
                      data-testid="new-horse-stable-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="breed">Breed</Label>
                    <Input 
                      id="breed"
                      value={newHorse.breed}
                      onChange={(e) => setNewHorse({...newHorse, breed: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="colour">Colour</Label>
                    <Input 
                      id="colour"
                      value={newHorse.colour}
                      onChange={(e) => setNewHorse({...newHorse, colour: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="horse_tab">Category</Label>
                    <Select value={newHorse.horse_tab} onValueChange={(v) => setNewHorse({...newHorse, horse_tab: v})}>
                      <SelectTrigger data-testid="new-horse-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_work">In Work</SelectItem>
                        <SelectItem value="spelling">Spelling</SelectItem>
                        <SelectItem value="breeding">Breeding</SelectItem>
                        <SelectItem value="youngstock">Youngstock</SelectItem>
                        <SelectItem value="former">Former</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sex">Sex</Label>
                    <Select value={newHorse.sex} onValueChange={(v) => setNewHorse({...newHorse, sex: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select sex" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mare">Mare</SelectItem>
                        <SelectItem value="gelding">Gelding</SelectItem>
                        <SelectItem value="stallion">Stallion</SelectItem>
                        <SelectItem value="colt">Colt</SelectItem>
                        <SelectItem value="filly">Filly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dob">Date of Birth</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      id="dob"
                      type="date"
                      className="pl-10"
                      value={newHorse.dob}
                      onChange={(e) => setNewHorse({...newHorse, dob: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="height">Height</Label>
                    <Input 
                      id="height"
                      value={newHorse.height}
                      onChange={(e) => setNewHorse({...newHorse, height: e.target.value})}
                      placeholder="e.g., 16.2hh"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="microchip">Microchip</Label>
                    <Input 
                      id="microchip"
                      value={newHorse.microchip}
                      onChange={(e) => setNewHorse({...newHorse, microchip: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="registration_number">Registration Number</Label>
                    <Input 
                      id="registration_number"
                      value={newHorse.registration_number}
                      onChange={(e) => setNewHorse({...newHorse, registration_number: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="registration_society">Registration Society</Label>
                    <Input 
                      id="registration_society"
                      value={newHorse.registration_society}
                      onChange={(e) => setNewHorse({...newHorse, registration_society: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sire">Sire (Father)</Label>
                    <Input 
                      id="sire"
                      value={newHorse.sire}
                      onChange={(e) => setNewHorse({...newHorse, sire: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dam">Dam (Mother)</Label>
                    <Input 
                      id="dam"
                      value={newHorse.dam}
                      onChange={(e) => setNewHorse({...newHorse, dam: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="breeder">Breeder</Label>
                    <Input 
                      id="breeder"
                      value={newHorse.breeder}
                      onChange={(e) => setNewHorse({...newHorse, breeder: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner">Owner</Label>
                    <Input 
                      id="owner"
                      value={newHorse.owner}
                      onChange={(e) => setNewHorse({...newHorse, owner: e.target.value})}
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-new-horse" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding Horse...
                    </>
                  ) : (
                    'Add Horse'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Search horses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white"
            data-testid="search-horses-input"
          />
        </div>

        {/* Tabs - 5 tabs: In Work, Spelling, Breeding, Youngstock, Former */}
        <Tabs defaultValue="in_work" className="w-full">
          <TabsList className="bg-muted mb-6 flex-wrap h-auto gap-1 p-1">
            {HORSE_TABS.map(tab => (
              <TabsTrigger 
                key={tab.value} 
                value={tab.value} 
                data-testid={`${tab.value}-horses-tab`}
                className="text-xs sm:text-sm"
              >
                {tab.label} ({tabCounts[tab.value] || 0})
              </TabsTrigger>
            ))}
          </TabsList>

          {HORSE_TABS.map(tab => (
            <TabsContent key={tab.value} value={tab.value}>
              {renderTabContent(tab.value)}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      
      {/* Upgrade prompt dialog */}
      <UpgradePromptDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        type="horses"
        currentCount={currentCount}
        limit={horseLimit}
      />
    </Layout>
  );
};
